import { Box, Flex, GridItem, Heading, Icon, Input, Spinner, Tag, Text } from "@chakra-ui/react";
import { useState } from "react";
import { FaClock, FaFlagCheckered } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { ProjectDto } from "../../../autogen/bff-api";
import { useEditor } from "../../../common/editing/save-coordinator";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { displayDateWithTime } from "../../../common/formatting/displayDateWithTime";
import { DateSelector } from "../../../common/input/DateTimeSelectors/DateSelector";
import { displayPersonName } from "../../contracts/view-single/sharing/AddExternalParticipantModal";
import { ProfileImageRounded } from "../../sourcing-events/buyer/view-all/ProfileImageRounded";

const TEXT_DEBOUNCE_MS = 200;

export const ProjectHeading = ({ projectId }: { projectId: string }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const { state: project, updateField, entityStatus } = useEditor<ProjectDto>(projectId);

  const [editName, setEditName] = useState(false);
  const [editExternalId, setEditExternalId] = useState(false);
  const [editStartDate, setEditStartDate] = useState(false);
  const [editEndDate, setEditEndDate] = useState(false);

  const isSaving = entityStatus === "pending" || entityStatus === "saving";

  return (
    <>
      <GridItem area="title" pt="2.5" px="2">
        {editName ? (
          <Flex>
            <Input
              value={project.name ?? ""}
              autoFocus
              onBlur={() => setEditName(false)}
              fontSize="xl"
              fontWeight="bold"
              maxWidth="60%"
              _focus={{ borderWidth: "1px", shadow: "none" }}
              height="8"
              px="2"
              onChange={(e) => updateField({ name: e.target.value }, { debounceMs: TEXT_DEBOUNCE_MS })}
            />
            {isSaving && <Spinner color="teal" ml="-8" mt="1" />}
          </Flex>
        ) : (
          <Heading
            as="h2"
            size="md"
            cursor={project.canEdit ? "pointer" : "auto"}
            onClick={() => {
              if (project.canEdit) setEditName(true);
            }}
            py="1"
          >
            {project.name ? (
              project.name
            ) : (
              <Box fontStyle="italic" color="smMuted">
                {t("Project name not provided")}
              </Box>
            )}
          </Heading>
        )}
        {editExternalId ? (
          <Flex>
            <Input
              value={project.externalId ?? ""}
              autoFocus
              onBlur={() => setEditExternalId(false)}
              maxWidth="20%"
              _focus={{ borderWidth: "1px", shadow: "none" }}
              fontSize="sm"
              height="5"
              px="1"
              m="0"
              onChange={(e) => updateField({ externalId: e.target.value }, { debounceMs: TEXT_DEBOUNCE_MS })}
            />
            {isSaving && <Spinner color="teal" ml="-5" mt="0.5" size="sm" />}
          </Flex>
        ) : (
          <Text
            color="smMuted"
            fontSize="sm"
            cursor={project.canEdit ? "pointer" : "auto"}
            onClick={() => {
              if (project.canEdit) setEditExternalId(true);
            }}
          >
            {project.externalId ? project.externalId : t("ID not provided")}
          </Text>
        )}
      </GridItem>
      <GridItem area="edit" pt="4" textAlign="end" px="2">
        <Tag colorScheme="teal">
          {t("Updated at")} {displayDateWithTime(project.modifiedAt, "")}
        </Tag>
      </GridItem>
      <GridItem area={"createdMeta"} pt="6" px="2">
        {project.projectResponsible && (
          <Flex>
            <Flex>
              <ProfileImageRounded
                name={project.projectResponsible?.firstName ?? project.name}
                width="5"
                backgroundColor="smPrimary"
                fontSize="xx-small"
              />
            </Flex>
            {project.projectResponsible && (
              <Flex justifyContent={"center"} mt="-3px" ml="5px">
                <Text fontSize={"lg"}>{displayPersonName(project.projectResponsible)}</Text>
              </Flex>
            )}
          </Flex>
        )}
      </GridItem>
      <GridItem
        area={"deadline"}
        borderBottom="1px solid"
        borderColor="smBorder"
        display="flex"
        flexDirection="column"
        pb="5"
        pt="1"
        px="2"
      >
        <Flex alignItems="center" pt="5">
          <Flex>
            <Icon as={FaClock} color="smPrimary" w="14px" h="14px" />
          </Flex>
          <Flex pl="1">
            {editStartDate ? (
              <DateSelector
                autoFocus
                defaultDate={project.startDate ?? undefined}
                onChange={(d) => updateField({ startDate: d || null })}
                onBlur={() => setEditStartDate(false)}
              />
            ) : (
              <Text
                fontSize="sm"
                color="smPrimary"
                cursor={project.canEdit ? "pointer" : "auto"}
                onClick={() => {
                  if (project.canEdit) setEditStartDate(true);
                }}
              >
                {t("Start date")}: {project.startDate ? displayDate(project.startDate) : t("None")}
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex alignItems="center" pt="2">
          <Flex>
            <Icon as={FaFlagCheckered} color="smSecondary" w="14px" h="14px" />
          </Flex>
          <Flex pl="1">
            {editEndDate ? (
              <DateSelector
                autoFocus
                defaultDate={project.endDate ?? undefined}
                onChange={(d) => updateField({ endDate: d || null })}
                onBlur={() => setEditEndDate(false)}
              />
            ) : (
              <Text
                fontSize="sm"
                color="smSecondary"
                cursor={project.canEdit ? "pointer" : "auto"}
                onClick={() => {
                  if (project.canEdit) setEditEndDate(true);
                }}
              >
                {t("End date")}: {project.endDate ? displayDate(project.endDate) : t("None")}
              </Text>
            )}
          </Flex>
        </Flex>
      </GridItem>
    </>
  );
};
